import * as yup from "yup";

import { Box, Grid } from '@mui/material';
import { EnhancedButtonPrimeReact, EnhancedButtonPrimeReactSeverity, EnhancedCheckboxRHFWithLabel, EnhancedDropDownRHFWithLabel, LabelPosition, RHookFormNumberFieldWithLabel, RHookFormTextFieldWithLabel, StandardPanel } from '@websential/cosmic-v20';
import { FormProvider, useForm } from 'react-hook-form';
import React, { useEffect, useState } from 'react'
import { sortDDLByLabel, useNavigateToLogout } from "../../../../common/Common";

import { IAppState } from "../../../../store/reducers";
import { MenuHeadsService } from "../../../../services/RoleManagementServices/MenuHeadsService";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";

const SINGLE_RECORD_DATA_QUERY_KEY = "departments-single-data-record";

interface ICategorySetupFormFields {
    categoryDDL: any;
    updatedCategoryDDL: (value: any) => void;
    onCategoryChangeHandler?: () => void;
    onCloseCategoryHandler: () => void;
    onDeleteMenuCategoryClicked: (id: any) => void;
    isLoadingDelete: boolean
}

const useGetData = (
    id: string | undefined,
    navigateToLogout: (path: string) => void
) =>
    useQuery({
        queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id, navigateToLogout],
        queryFn: () => {
            return MenuHeadsService.getMenuCategoryById(id, navigateToLogout);
        },
        refetchOnWindowFocus: false,
    });

export const CategorySetupFormFields: React.FC<ICategorySetupFormFields> = ({
    categoryDDL,
    updatedCategoryDDL,
    onCloseCategoryHandler,
    onDeleteMenuCategoryClicked,
    isLoadingDelete
}) => {
    const navigateToLogout = useNavigateToLogout();

    const [isLoadingAPI, setIsLoadingAPI] =
        useState<any>(false);

    const [loadedCategory, setLoadedCategory] =
        useState<any>(undefined);
    const [categoryDDLState, setCategoryDDLState] =
        useState<any>(undefined);
    useEffect(() => {
        if (categoryDDL) {
            let categoryList: any[] =
                sortDDLByLabel(categoryDDL).map((category: any) => ({
                    label: category.label,
                    value: category.value
                }));

            categoryList.unshift({
                label: "Create New",
                value: "create-new",
            });

            setCategoryDDLState(categoryList);
        }
    }, [categoryDDL]);

    const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
        loadedCategory,
        navigateToLogout
    );


    const validationSchema = yup.object({
        categoryName: yup.string().required('Name is required'),
    });

    const formMethods = useForm<any>({
        resolver: yupResolver(validationSchema),
        mode: "onBlur",
        reValidateMode: "onChange",
    });

    const onSubmit = async (values: any) => {
        (async () => {
            try {
                setIsLoadingAPI(true)
                const response: any =
                    await MenuHeadsService.createMenuCategory(loadedCategory, values);
                if (response.status === 201 || response.status === 200) {
                    formMethods.setValue("loadCategory", response.data.id);
                    setLoadedCategory(response.data.id);
                    (async () => {
                        try {
                            setIsLoadingAPI(true)
                            const response: any =
                                await MenuHeadsService.getMenuCategoryDDL();
                            let categoryList: any[] =
                                response.map((category: any) => ({
                                    label: category.label,
                                    value: category.value
                                }));

                            categoryList.unshift({
                                label: "Create New",
                                value: "create-new",
                            });
                            updatedCategoryDDL(response)
                            setCategoryDDLState(categoryList)
                        } catch { } finally {
                            setIsLoadingAPI(false)
                        }
                    })();
                }
            } catch { } finally {
                setIsLoadingAPI(false)
            }
        })();
    }

    const onCategoryChangeHandler = (item: any) => {
        if (item.value !== "create-new") {
            setLoadedCategory(item.value)
        } else {
            setLoadedCategory(undefined)
            formMethods.setValue("categoryName", undefined);
        }
    }


    useEffect(() => {
        if (loadedCategory && selectedDataRecord) {
            Object.keys(selectedDataRecord).forEach((fieldName) => {
                const fieldValue = selectedDataRecord[fieldName];
                formMethods.setValue(fieldName, fieldValue ?? undefined);
                if (fieldName === 'id') {
                    formMethods.setValue("loadCategory", fieldValue);
                }
            });
        }
    }, [loadedCategory, selectedDataRecord]);


    return (
        <Grid sx={{ "& .numberFieldWrapper": { marginBottom: "0!important" } }}>
            <FormProvider {...formMethods}>
                <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                    <StandardPanel title={''} showTitle={false} testId={"undefined"}>
                        <EnhancedDropDownRHFWithLabel
                            selectName="loadCategory"
                            label="Load Category"
                            labelPosition={LabelPosition.Left}
                            // required
                            testId={`category-list`}
                            items={categoryDDLState ?? []}
                            // items={[]}
                            labelColumns={4}
                            fieldColumns={8}
                            onSelectionChangeHandler={onCategoryChangeHandler}
                        />
                        <RHookFormTextFieldWithLabel
                            label="Name"
                            id="categoryName"
                            testId={`category-title`}
                            labelPosition={LabelPosition.Left}
                            labelColumns={4}
                            fieldColumns={8}
                        />
                    </StandardPanel>

                    <Box sx={{ display: "flex", justifyContent: "flex-end", padding: "16px", paddingBottom: 0 }}>
                        <EnhancedButtonPrimeReact
                            label="Cancel"
                            severity="secondary"
                            text
                            raised
                            onClickHandler={onCloseCategoryHandler}
                            style={{ fontSize: "small" }}
                        />
                        <EnhancedButtonPrimeReact
                            label="Delete"
                            raised
                            onClickHandler={() => onDeleteMenuCategoryClicked(loadedCategory)}
                            severity={EnhancedButtonPrimeReactSeverity.Danger}
                            disabled={!loadedCategory ? true : isLoadingDelete ? true : false}
                            style={{ marginLeft: "16px", fontSize: "small" }}
                            loading={isLoadingDelete}
                        />
                        <EnhancedButtonPrimeReact
                            label="Save"
                            type="submit"
                            raised
                            severity={EnhancedButtonPrimeReactSeverity.Success}
                            style={{ marginLeft: "16px", fontSize: "small" }}
                            loading={isLoadingAPI}
                        />
                    </Box>

                </form>
            </FormProvider>
        </Grid>
    );
}
